import type { AISetting, CatalogRule } from '@repo/api-codegen';
import { uuidv4 } from 'lib0/random';
import {
	makeAutoObservable,
	observable,
	ObservableMap,
	runInAction,
} from 'mobx';
import type { ISecodaEntity } from '../../api';
import type { AiOutputPreviewData, AiOutputPreviewDataItem } from './types';

export interface AiSettingAction extends Partial<AISetting> {
	id: string;
	type?: string;
	prompt?: string;
	resources_to_preview?: string[];
	bind_pii?: boolean | null;
	bind_tags?: boolean | null;
	bind_owners?: boolean | null;
	bind_description?: boolean | null;
	bind_definition?: boolean | null;
}

export class AiSettingStore {
	aiOutputBlockStore: AiOutputBlockStore;
	aiSettingAction: AiSettingAction;
	isSettingReady: boolean = false;

	displayTypeError: boolean = false;
	displayPromptError: boolean = false;
	displayBindError: boolean = false;
	displayResourcesToPreviewError: boolean = false;

	constructor(
		aiOutputBlockStore: AiOutputBlockStore,
		aiSettingAction: AiSettingAction
	) {
		makeAutoObservable(this, {}, { autoBind: true });

		this.aiOutputBlockStore = aiOutputBlockStore;
		this.aiSettingAction = observable.object(aiSettingAction);
	}

	setType(type?: string) {
		runInAction(() => {
			this.aiSettingAction.type = type;
			// Apply template settings based on type
			this.applyTemplate(type);
		});

		this.checkIsSettingReady();
	}

	applyTemplate(type?: string) {
		// Reset all bindings first
		this.aiSettingAction.bind_pii = false;
		this.aiSettingAction.bind_tags = false;
		this.aiSettingAction.bind_owners = false;
		this.aiSettingAction.bind_description = false;
		this.aiSettingAction.bind_definition = false;

		switch (type) {
			case 'description':
				this.aiSettingAction.prompt = `Write a brief, direct description of what this resource is and its business purpose.

**RULES**:
- Maximum 15-20 words
- No filler phrases like "This table contains..." or "Used for storing..."
- Start directly with the content/purpose
- Focus on business value, not technical details

**GOOD EXAMPLES**:
✓ "Customer purchase history for revenue analytics"
✓ "Daily inventory snapshots from warehouse systems"
✓ "Marketing campaign performance metrics"

**BAD EXAMPLES**:
✗ "This table contains customer information including their purchase history and is used by the analytics team"
✗ "Stores data about inventory including quantities and locations"

Get straight to the point. What is it and why does it matter?`;
				this.aiSettingAction.bind_description = true;
				break;

			case 'documentation':
				this.aiSettingAction.prompt = `Create comprehensive documentation for each resource following this structure:

1. **Overview**: Brief summary of the resource's purpose and importance
2. **Data Content**: Key fields/columns and what they represent
3. **Relationships**: How this connects to other tables/resources (if apparent from lineage)
4. **Usage Context**: Common use cases and business processes it supports
5. **Data Characteristics**: Update frequency, data quality considerations, or notable patterns

Be thorough but organized. Use clear headings and bullet points where appropriate. Focus on information that would help a new user understand and use this resource effectively.

Keep technical details accurate but accessible to non-technical stakeholders.`;
				this.aiSettingAction.bind_definition = true;
				break;

			case 'pii':
				this.aiSettingAction.prompt = `Analyze each resource for Personally Identifiable Information (PII) and apply BOTH the PII flag AND specific PII type tags:

**STEP 1 - SET PII FLAG (true/false)**:
Mark PII = true if resource contains ANY of these:
- Names (first, last, full)
- Contact info (email, phone, address)
- Government IDs (SSN, passport, license)
- Financial identifiers (account numbers, credit cards)
- Medical/health information
- Biometric data
- Device IDs that can identify individuals
- Any combination that could identify a person

**STEP 2 - ADD SPECIFIC PII TAGS**:
When PII = true, ALSO add specific tags for the types of PII found:
- "PII-Name" → First name, last name, full name
- "PII-Email" → Email addresses
- "PII-Phone" → Phone numbers
- "PII-Address" → Physical addresses, postal codes
- "PII-SSN" → Social Security Numbers
- "PII-Financial" → Credit cards, bank accounts
- "PII-Medical" → Health records, medical IDs
- "PII-DOB" → Date of birth
- "PII-Government-ID" → Passport, driver's license, etc.

**EXAMPLES**:
✓ Table with customer names and emails:
  - PII: true
  - Tags: "PII-Name", "PII-Email"

✓ Table with just email addresses:
  - PII: true
  - Tags: "PII-Email"

✓ Table with addresses and phone numbers:
  - PII: true
  - Tags: "PII-Address", "PII-Phone"

✓ Aggregated sales metrics:
  - PII: false
  - Tags: none

**IMPORTANT**:
- Always set BOTH the PII flag AND specific PII type tags when PII is found
- Be thorough - tag ALL types of PII present in the resource
- Look at column names and data patterns to identify PII types`;
				this.aiSettingAction.bind_pii = true;
				break;

			case 'tags':
				this.aiSettingAction.prompt = `Apply data governance, classification, and PII tags with EXTREME SELECTIVITY:

**PART 1 - PII DETECTION & TAGS**:
First, check for PII and apply specific PII tags if found:
- "PII-Name" → Contains first/last/full names
- "PII-Email" → Contains email addresses
- "PII-Phone" → Contains phone numbers
- "PII-Address" → Contains physical addresses
- "PII-SSN" → Contains Social Security Numbers
- "PII-Financial" → Contains credit cards, bank accounts
- "PII-Medical" → Contains health records
- "PII-DOB" → Contains dates of birth
- "PII-Government-ID" → Contains passport, license numbers

Also set PII flag = true if ANY PII tags are applied.

**PART 2 - GOVERNANCE TAGS**:
Then apply governance tags ONLY when needed for access control:
- **Sensitive** → Business-critical or confidential information
- **Restricted** → Requires access controls due to regulations
- **Internal** → Internal-use only, not for external exposure
- **Public** → Can be safely shared externally
- **Financial** → Financial data requiring audit trails
- **Regulatory** → Subject to specific compliance (SOX, HIPAA, etc.)
- **Critical** → Essential for operations, needs high availability

**STRICT RULES**:
1. Maximum 3-4 total tags per resource (including PII tags)
2. Must be 95%+ confident for any tag
3. Default to NO tags unless clear need exists
4. Apply PII tags first, then governance tags if needed

**EXAMPLES**:
✓ "customer_profiles" with names and emails:
  - PII: true
  - Tags: "PII-Name", "PII-Email", "Restricted"

✓ "financial_statements":
  - PII: false
  - Tags: "Sensitive", "Financial"

✓ "employee_ssn_records":
  - PII: true
  - Tags: "PII-SSN", "PII-Name", "Restricted", "Regulatory"

✓ "product_catalog":
  - PII: false
  - Tags: "Public"

✓ "system_logs":
  - PII: false
  - Tags: none (unless contains user data)

**DO NOT TAG**:
- Development/test data
- Derived or aggregated data (unless inheriting sensitivity)
- Technical metadata tables
- Resources where classification adds no governance value

Remember: Tags drive access controls and compliance. Only tag when the classification will actually impact how the data is governed.`;
				this.aiSettingAction.bind_tags = true;
				this.aiSettingAction.bind_pii = true;
				break;

			case 'custom':
				this.aiSettingAction.prompt = `[Your custom instructions here]

**TIPS FOR CUSTOM PROMPTS**:
- Be specific about what you want to achieve
- Include examples of desired output format
- Specify any constraints or rules to follow
- Indicate which fields should be updated
- Use clear, action-oriented language

Select the appropriate fields to update below based on your prompt.`;
				// Keep all bindings false for custom - user will select manually
				break;

			default:
				this.aiSettingAction.prompt = '';
				break;
		}
	}

	setPrompt(prompt: string) {
		this.aiSettingAction.prompt = prompt;
		this.checkIsSettingReady();
	}

	// Override binding setters to reset errors when changed
	setBindPii(bind: boolean) {
		this.aiSettingAction.bind_pii = bind;
		this.displayBindError = false;
		this.checkIsSettingReady();
	}

	setBindTags(bind: boolean) {
		this.aiSettingAction.bind_tags = bind;
		this.displayBindError = false;
		this.checkIsSettingReady();
	}

	setBindOwners(bind: boolean) {
		this.aiSettingAction.bind_owners = bind;
		this.displayBindError = false;
		this.checkIsSettingReady();
	}

	setBindDescription(bind: boolean) {
		this.aiSettingAction.bind_description = bind;
		this.displayBindError = false;
		this.checkIsSettingReady();
	}

	setBindDefinition(bind: boolean) {
		this.aiSettingAction.bind_definition = bind;
		this.displayBindError = false;
		this.checkIsSettingReady();
	}

	setResourcesToPreview(resourcesToPreview: string[]) {
		this.aiSettingAction.resources_to_preview = resourcesToPreview;
		this.checkIsSettingReady();
	}

	checkIsSettingReady() {
		this.isSettingReady =
			!!this.aiSettingAction.type && !!this.aiSettingAction.prompt;
	}

	resetErrors() {
		this.displayTypeError = false;
		this.displayPromptError = false;
		this.displayBindError = false;
		this.displayResourcesToPreviewError = false;
	}

	checkIsPreviewReady() {
		if (!this.aiSettingAction.type) {
			this.displayTypeError = true;
		}

		if (!this.aiSettingAction.prompt) {
			this.displayPromptError = true;
		}

		if (
			!this.aiSettingAction.resources_to_preview ||
			this.aiSettingAction.resources_to_preview?.length === 0
		) {
			this.displayResourcesToPreviewError = true;
		}

		if (
			this.displayTypeError ||
			this.displayPromptError ||
			this.displayResourcesToPreviewError
		) {
			return false;
		}

		return true;
	}

	checkIsSubmitReady() {
		if (!this.aiSettingAction.type) {
			this.displayTypeError = true;
		}

		if (!this.aiSettingAction.prompt) {
			this.displayPromptError = true;
		}

		const hasAnyBind = Boolean(
			this.aiSettingAction.bind_pii ||
				this.aiSettingAction.bind_tags ||
				this.aiSettingAction.bind_owners ||
				this.aiSettingAction.bind_description ||
				this.aiSettingAction.bind_definition
		);

		if (!hasAnyBind) {
			this.displayBindError = true;
		}

		if (
			this.displayTypeError ||
			this.displayPromptError ||
			this.displayBindError
		) {
			return false;
		}

		return true;
	}
}

export class AiOutputBlockStore {
	aiSettings: AISetting[];
	aiSettingsMap: ObservableMap<string, AiSettingStore> = new ObservableMap();
	hasAiSettings: boolean = false;
	filterRule: CatalogRule;
	enableAIOutputReview: boolean;
	aiSettingTypes: { label: string; value: string }[] = [
		{ label: 'Description', value: 'description' },
		{ label: 'Documentation', value: 'documentation' },
		{ label: 'PII Detection', value: 'pii' },
		{ label: 'Tags', value: 'tags' },
		{ label: 'Custom', value: 'custom' },
	];

	currentAiSettingId: string | undefined = undefined;

	searchTerm: string = '';

	isPreviewLoading: boolean = false;
	previewJobId: string | undefined = undefined;
	previewData: AiOutputPreviewData | undefined = undefined;
	currentPreviewItem: AiOutputPreviewDataItem | undefined = undefined;
	currentResource: ISecodaEntity | undefined = undefined;
	previewError: unknown = null;
	previewIndex: number = 0;

	// Track feedback state per preview item (keyed by entity ID + AI setting ID)
	feedbackState: ObservableMap<string, 'positive' | 'negative'> =
		new ObservableMap();

	constructor(
		initialAiSettings: AISetting[],
		initialFilterRule: CatalogRule,
		initialEnableAIOutputReview: boolean
	) {
		makeAutoObservable(this, {}, { autoBind: true });

		this.aiSettings = initialAiSettings;
		this.setAiSettings();
		this.filterRule = initialFilterRule;
		this.enableAIOutputReview = initialEnableAIOutputReview;
	}

	setAiSettings() {
		for (const aiSetting of this.aiSettings) {
			const aiSettingStore = this.aiSettingsMap.get(aiSetting.id);
			if (!aiSettingStore) {
				this.aiSettingsMap.set(
					aiSetting.id,
					new AiSettingStore(this, {
						id: aiSetting.id,
						type: aiSetting.type,
						prompt: aiSetting.prompt,
						resources_to_preview: aiSetting.resources_to_preview,
						bind_pii: aiSetting.bind_pii ?? false,
						bind_tags: aiSetting.bind_tags ?? false,
						bind_owners: aiSetting.bind_owners ?? false,
						bind_description: aiSetting.bind_description ?? false,
						bind_definition: aiSetting.bind_definition ?? false,
					})
				);
			}
		}

		this.hasAiSettings = this.aiSettingsMap.size > 0;
	}

	addAiSetting = (): string => {
		const id = uuidv4();

		this.aiSettingsMap.set(
			id,
			new AiSettingStore(this, {
				id,
				type: undefined,
				prompt: '',
				resources_to_preview: [],
				bind_pii: false,
				bind_tags: false,
				bind_owners: false,
				bind_description: false,
				bind_definition: false,
			})
		);

		return id;
	};

	deleteAiSetting = (key: string) => {
		this.aiSettingsMap.delete(key);
		this.updateAiSettings();
	};

	updateAiSettings() {
		const newSettings: AISetting[] = [];

		this.aiSettingsMap.forEach((aiSettingStore) => {
			if (
				aiSettingStore &&
				aiSettingStore.aiSettingAction &&
				aiSettingStore.aiSettingAction.id &&
				aiSettingStore.aiSettingAction.type &&
				aiSettingStore.aiSettingAction.prompt
			) {
				newSettings.push({
					id: aiSettingStore.aiSettingAction.id,
					type: aiSettingStore.aiSettingAction.type,
					prompt: aiSettingStore.aiSettingAction.prompt,
					resources_to_preview:
						aiSettingStore.aiSettingAction.resources_to_preview ?? [],
					bind_pii: aiSettingStore.aiSettingAction.bind_pii ?? false,
					bind_tags: aiSettingStore.aiSettingAction.bind_tags ?? false,
					bind_owners: aiSettingStore.aiSettingAction.bind_owners ?? false,
					bind_description:
						aiSettingStore.aiSettingAction.bind_description ?? false,
					bind_definition:
						aiSettingStore.aiSettingAction.bind_definition ?? false,
				});
			}
		});

		this.aiSettings = newSettings;
		this.hasAiSettings = this.aiSettingsMap.size > 0;
	}

	setEnableAIOutputReview(enableAIOutputReview: boolean) {
		this.enableAIOutputReview = enableAIOutputReview;
	}

	setCurrentAiSettingId(id: string | undefined) {
		this.currentAiSettingId = id;
	}

	getCurrentAiSettingStore() {
		return this.currentAiSettingId
			? this.aiSettingsMap.get(this.currentAiSettingId)
			: null;
	}

	setSearchTerm(term: string) {
		this.searchTerm = term;
	}

	setIsPreviewLoading(loading: boolean) {
		this.isPreviewLoading = loading;
	}

	setPreviewJobId(jobId: string | undefined) {
		this.previewJobId = jobId;
	}

	setPreviewData(data: AiOutputPreviewData | undefined) {
		this.previewData = data;
	}

	setCurrentPreviewItem(item: AiOutputPreviewDataItem | undefined) {
		this.currentPreviewItem = item;
	}

	setCurrentResource(resource: ISecodaEntity | undefined) {
		this.currentResource = resource;
	}

	setPreviewError(error: unknown) {
		this.previewError = error;
	}

	setPreviewIndex(index: number) {
		this.previewIndex = index;
	}

	resetPreviewState() {
		this.isPreviewLoading = false;
		this.previewJobId = undefined;
		this.previewData = undefined;
		this.currentPreviewItem = undefined;
		this.currentResource = undefined;
		this.previewError = null;
		this.previewIndex = 0;
		this.feedbackState.clear();
	}

	getCurrentFeedbackKey(): string | null {
		if (!this.currentResource || !this.currentAiSettingId) {
			return null;
		}
		return `${this.currentResource.id}-${this.currentAiSettingId}`;
	}

	setCurrentFeedback(sentiment: 'positive' | 'negative') {
		const key = this.getCurrentFeedbackKey();
		if (key) {
			runInAction(() => {
				this.feedbackState.set(key, sentiment);
			});
		}
	}

	getCurrentFeedback(): 'positive' | 'negative' | null {
		const key = this.getCurrentFeedbackKey();
		if (key) {
			return this.feedbackState.get(key) || null;
		}
		return null;
	}

	hasCurrentFeedback(): boolean {
		return this.getCurrentFeedback() !== null;
	}

	resetModalState() {
		this.currentAiSettingId = undefined;
		this.searchTerm = '';
		this.resetPreviewState();
	}
}
